<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  open: { type: Boolean, default: false },
  anchorRect: { type: Object, default: null },
});
const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const mode = ref('quick');
const openedAt = ref(Date.now());
const selectedDate = ref(new Date());
const visibleMonth = ref(new Date());
const dateText = ref('');
const timeText = ref('');
const error = ref('');
const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const formatDate = date =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
const formatTime = date =>
  new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
const atTime = (date, hour, minute = 0) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute);
const futureWeekday = (now, weekday) => {
  const days = (weekday - now.getDay() + 7) % 7 || 7;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
};

watch(
  () => props.open,
  open => {
    if (!open) return;
    openedAt.value = Date.now();
    mode.value = 'quick';
    error.value = '';
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    selectedDate.value = tomorrow;
    visibleMonth.value = new Date(
      tomorrow.getFullYear(),
      tomorrow.getMonth(),
      1
    );
    dateText.value = formatDate(tomorrow);
    timeText.value = '08:00';
  }
);

const quickChoices = computed(() => {
  const now = new Date(openedAt.value);
  const today = atTime(now, 18);
  const tomorrow = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  );
  const weekend =
    now.getDay() === 6 ? atTime(tomorrow, 8) : atTime(futureWeekday(now, 6), 8);
  return [
    ...(today > now ? [{ key: 'SNOOZE_LATER_TODAY', at: today }] : []),
    { key: 'SNOOZE_TOMORROW', at: atTime(tomorrow, 8) },
    ...(now.getDay() === 0
      ? []
      : [{ key: 'SNOOZE_THIS_WEEKEND', at: weekend }]),
    { key: 'SNOOZE_NEXT_WEEK', at: atTime(futureWeekday(now, 1), 8) },
  ];
});
const menuStyle = computed(() => {
  const width = Math.min(280, window.innerWidth - 16);
  const rect = props.anchorRect;
  if (!rect) return { left: `calc(50% - ${width / 2}px)`, top: '25%' };
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const top =
    rect.bottom + 8 + 300 > window.innerHeight
      ? Math.max(8, rect.top - 300)
      : rect.bottom + 8;
  return { left: `${left}px`, top: `${top}px` };
});
const monthTitle = computed(() =>
  new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
    visibleMonth.value
  )
);
const calendarDays = computed(() => {
  const year = visibleMonth.value.getFullYear();
  const month = visibleMonth.value.getMonth();
  const offset = new Date(year, month, 1).getDay();
  return Array.from(
    { length: 42 },
    (_, index) => new Date(year, month, 1 - offset + index)
  );
});
const sameDay = (first, second) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();
const isPast = date => atTime(date, 23, 59) < new Date();
const shiftMonth = offset => {
  visibleMonth.value = new Date(
    visibleMonth.value.getFullYear(),
    visibleMonth.value.getMonth() + offset,
    1
  );
};
const chooseDate = date => {
  selectedDate.value = date;
  visibleMonth.value = new Date(date.getFullYear(), date.getMonth(), 1);
  dateText.value = formatDate(date);
  error.value = '';
};
const parseDate = raw => {
  const value = raw.trim();
  const local = value.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/);
  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!local && !iso) return null;
  const day = Number(local ? local[1] : iso[3]);
  const month = Number(local ? local[2] : iso[2]);
  let year = Number(local ? local[3] || new Date().getFullYear() : iso[1]);
  if (year < 100) year += 2000;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  )
    return null;
  return date;
};
const parseTime = raw => {
  const value = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  const match = value.match(
    /^(\d{1,2})(?:\s*[:h.]\s*(\d{1,2}))?\s*(am|pm|a\.?m\.?|p\.?m\.?|da manh[aã]|da tarde|da noite)?$/i
  );
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const period = (match[3] || '').replace(/\./g, '');
  if (minute > 59 || hour > 23) return null;
  if (period) {
    if (hour < 1 || hour > 12) return null;
    if (/pm|tarde|noite/.test(period)) hour = (hour % 12) + 12;
    else hour %= 12;
  }
  return { hour, minute };
};
const syncTypedDate = () => {
  const date = parseDate(dateText.value);
  if (date) chooseDate(date);
};
const normalizeTypedTime = () => {
  const time = parseTime(timeText.value);
  if (time)
    timeText.value = `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
};
const confirmQuick = choice => {
  if (choice.at <= new Date()) {
    openedAt.value = Date.now();
    return;
  }
  emit('confirm', choice.at.toISOString());
};
const confirmManual = () => {
  const date = parseDate(dateText.value);
  if (!date) {
    error.value = t('INBOX.EMAIL.SNOOZE_INVALID_DATE');
    return;
  }
  const time = parseTime(timeText.value);
  if (!time) {
    error.value = t('INBOX.EMAIL.SNOOZE_INVALID_TIME');
    return;
  }
  const until = atTime(date, time.hour, time.minute);
  if (
    until <= new Date() ||
    until.getHours() !== time.hour ||
    until.getMinutes() !== time.minute
  ) {
    error.value = t('INBOX.EMAIL.SNOOZE_FUTURE');
    return;
  }
  error.value = '';
  emit('confirm', until.toISOString());
};
</script>

<template>
  <div v-show="open" class="fixed inset-0 z-[110]">
    <div
      v-if="mode === 'quick'"
      class="absolute inset-0"
      @click="emit('close')"
    >
      <div
        class="absolute max-h-[calc(100vh-16px)] w-[280px] max-w-[calc(100vw-16px)] overflow-y-auto rounded-xl border border-[#e0e3e7] bg-white py-2 text-[#202124] shadow-xl"
        :style="menuStyle"
        role="menu"
        :aria-label="t('INBOX.EMAIL.SNOOZE')"
        @click.stop
      >
        <p class="px-4 py-2 text-xs font-semibold text-[#5f6368]">
          {{ t('INBOX.EMAIL.SNOOZE_UNTIL') }}
        </p>
        <button
          v-for="choice in quickChoices"
          :key="choice.key"
          type="button"
          role="menuitem"
          class="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm hover:bg-[#f1f3f4]"
          @click="confirmQuick(choice)"
        >
          <span>{{ t(`INBOX.EMAIL.${choice.key}`) }}</span>
          <span class="shrink-0 text-xs text-[#5f6368]">{{
            formatTime(choice.at)
          }}</span>
        </button>
        <button
          type="button"
          role="menuitem"
          class="mt-2 flex w-full items-center gap-3 border-t border-[#e0e3e7] px-4 pt-3 pb-1 text-left text-sm hover:bg-[#f1f3f4]"
          @click="mode = 'manual'"
        >
          <span
            class="i-lucide-calendar-days size-4 text-[#5f6368]"
            aria-hidden="true"
          />{{ t('INBOX.EMAIL.SNOOZE_PICK_DATE_TIME') }}
        </button>
      </div>
    </div>
    <div
      v-else
      class="absolute inset-0 flex items-center justify-center overflow-y-auto bg-black/30 p-4"
      @click.self="emit('close')"
    >
      <form
        class="max-h-[calc(100vh-2rem)] w-full max-w-[560px] overflow-y-auto rounded-2xl bg-white p-5 text-[#202124] shadow-2xl"
        role="dialog"
        aria-modal="true"
        :aria-label="t('INBOX.EMAIL.SNOOZE_PICK_DATE_TIME')"
        @submit.prevent="confirmManual"
      >
        <h2 class="text-lg font-semibold">
          {{ t('INBOX.EMAIL.SNOOZE_PICK_DATE_TIME') }}
        </h2>
        <div class="mt-5 grid gap-5 sm:grid-cols-[1fr_220px]">
          <div>
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium capitalize">{{
                monthTitle
              }}</span>
              <div class="flex gap-1">
                <button
                  type="button"
                  class="rounded-full p-2 hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.PREVIOUS_MONTH')"
                  @click="shiftMonth(-1)"
                >
                  <span
                    class="i-lucide-chevron-left size-4"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  class="rounded-full p-2 hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.NEXT_MONTH')"
                  @click="shiftMonth(1)"
                >
                  <span
                    class="i-lucide-chevron-right size-4"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
            <div class="mt-2 grid grid-cols-7 text-center text-xs">
              <span
                v-for="(day, index) in weekdays"
                :key="day"
                class="py-2 font-medium"
                :class="
                  index === 0 || index === 6 ? 'text-red-600' : 'text-[#5f6368]'
                "
                >{{ day }}</span
              >
              <button
                v-for="day in calendarDays"
                :key="day.toISOString()"
                type="button"
                class="mx-auto flex size-8 items-center justify-center rounded-full text-xs disabled:opacity-30"
                :class="
                  sameDay(day, selectedDate)
                    ? 'bg-[#15603e] text-white'
                    : day.getMonth() !== visibleMonth.getMonth()
                      ? 'text-[#9aa0a6] hover:bg-[#f1f3f4]'
                      : day.getDay() % 6 === 0
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-[#202124] hover:bg-[#f1f3f4]'
                "
                :disabled="isPast(day)"
                @click="chooseDate(day)"
              >
                {{ day.getDate() }}
              </button>
            </div>
          </div>
          <div class="flex flex-col gap-3">
            <label
              class="text-xs font-medium text-[#5f6368]"
              for="email-snooze-date"
              >{{ t('INBOX.EMAIL.SNOOZE_DATE') }}</label
            >
            <input
              id="email-snooze-date"
              v-model="dateText"
              type="text"
              inputmode="numeric"
              class="h-12 w-full rounded-lg border border-[#dadce0] bg-white px-3 text-sm text-[#202124] outline-none focus:border-[#16834b]"
              :placeholder="t('INBOX.EMAIL.SNOOZE_DATE_PLACEHOLDER')"
              @blur="syncTypedDate"
            />
            <label
              class="mt-2 text-xs font-medium text-[#5f6368]"
              for="email-snooze-time"
              >{{ t('INBOX.EMAIL.SNOOZE_TIME') }}</label
            >
            <input
              id="email-snooze-time"
              v-model="timeText"
              type="text"
              inputmode="text"
              class="h-12 w-full rounded-lg border border-[#dadce0] bg-white px-3 text-sm text-[#202124] outline-none focus:border-[#16834b]"
              :placeholder="t('INBOX.EMAIL.SNOOZE_TIME_PLACEHOLDER')"
              @blur="normalizeTypedTime"
            />
            <p class="text-xs text-[#5f6368]">
              {{ t('INBOX.EMAIL.SNOOZE_TIME_HINT') }}
            </p>
          </div>
        </div>
        <p v-if="error" class="mt-3 text-xs text-red-600" role="alert">
          {{ error }}
        </p>
        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm text-[#5f6368] hover:bg-[#f1f3f4]"
            @click="emit('close')"
          >
            {{ t('INBOX.EMAIL.CANCEL') }}
          </button>
          <button
            type="submit"
            class="rounded-full bg-[#15603e] px-5 py-2 text-sm font-semibold text-white hover:bg-[#104b31]"
          >
            {{ t('INBOX.EMAIL.SNOOZE_SAVE') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
